import { intl } from 'containers/LanguageProvider/IntlGlobalProvider';
import compareDocumentItems from 'builder/Knowledge/components/TreeView/compareDocumentItems';
import messages from "builder/Knowledge/components/TreeView/messages";
import { 
  competencyDocumentItemPrefixes,
  competencyDocumentItemSufixes,
  knowledgeMapDetailInputStates,
} from 'constants/knowledgeMap';
import { 
  calculatePrefixNumber,
} from './generatePrefix';
import {
  buildCompetencyDocumentCopyText,
} from './KnowledgeMapDuplicateParser';

/**
 * insert new competency document item.
 * 
 * @param {object} competencyDocumentItem
 * @param {object} expandedById
 * @param {object} competencyTree
 * @param {object} competencyDocument
 * @returns {object} - new competency document item.
 */
export const insertCompetencyDocumentItem = (
  competencyDocumentItem, 
  expandedById,
  competencyTree,
  competencyDocument,
) => {
  const competencyDocumentParentItems = competencyTree.getRootItems().sort(compareDocumentItems);
  const { id: competencyItemId } = competencyDocumentItem;
  
  const isParentItem = competencyDocumentParentItems.find(
    (parentItem) => parentItem.id === competencyItemId
  );

  //add node for a parent item.
  if (isParentItem) {
    return addParentItemNode(
      competencyTree,
      competencyDocument,
      competencyDocumentParentItems,
      competencyDocumentItem,
      expandedById,
    );
  } 
  
  //add node for root item.
  if (competencyTree.isCompetencyDocument(competencyItemId)) {
    return addRootItemNode(
      competencyTree,
      competencyDocument,
      competencyDocumentParentItems,
    );
  } 
  
  //add node for a child item.
  return addChildItemNode(
    competencyTree, 
    competencyItemId, 
    competencyDocumentItem,
    competencyDocument,
  );
};

/**
 * create new competency document item node for child item.
 * 
 * @param {object} competencyTree
 * @param {number} competencyItemId
 * @param {object} competencyDocumentItem
 * @param {object} competencyDocument
 * @returns {object} - new competency document item.
 */
export const addChildItemNode = (
  competencyTree, 
  competencyItemId, 
  competencyDocumentItem,
  competencyDocument,
) => {
  const [ parentItemId ] = competencyTree.getAllParentsByDeepChildId(competencyItemId);
  const currentChildSibblings = competencyTree.getChildren(parentItemId)
    .sort(compareDocumentItems);
  const newItemPrefixNumber = calculateNewItemPrefix(
    currentChildSibblings, 
    competencyDocumentItem, 
    competencyTree
  );

  return createCompetencyItemObject(
    newItemPrefixNumber,
    false, //create child item
    competencyTree,
    competencyDocument,
    parentItemId,
  );
};

/**
 * create new competency document item node for Root item.
 * 
 * @param {object} competencyTree
 * @param {object} competencyDocument
 * @param {Array} competencyDocumentParentItems
 * @returns {object} - new competency document item.
 */
export const addRootItemNode = (
  competencyTree,
  competencyDocument,
  competencyDocumentParentItems,
) => {
  let newItemPrefixNumber = 1;
  
  if (competencyDocumentParentItems.length > 0) {
    newItemPrefixNumber = calculatePrefixNumber( 
      competencyTree,
      competencyDocumentParentItems[0]
    );
  }

  return createCompetencyItemObject(
    newItemPrefixNumber,
    true, //create parent item
    competencyTree,
    competencyDocument
  );
};

/**
 * create new competency document item node for Parent item.
 * 
 * @param {object} competencyTree
 * @param {object} competencyDocument
 * @param {Array} competencyDocumentParentItems
 * @param {object} competencyDocumentItem
 * @param {object} expandedById
 * @returns {object} - new competency document item.
 */
export const addParentItemNode = (
  competencyTree,
  competencyDocument,
  competencyDocumentParentItems,
  competencyDocumentItem,
  expandedById,
) => {
  const isParentItemExpanded = expandedById[competencyDocumentItem.id];
  
  if (isParentItemExpanded) {
    return addExpandedParentItemNode(
      competencyTree, 
      competencyDocumentItem.id,
      competencyDocument,
    );
  } 
    
  const newItemPrefixNumber = calculateNewItemPrefix(
    competencyDocumentParentItems, 
    competencyDocumentItem, 
    competencyTree
  );
  
  return createCompetencyItemObject(
    newItemPrefixNumber,
    true, //create parent item
    competencyTree,
    competencyDocument
  );
};

/**
 * create new competency document item node for Expanded Parent item.
 * 
 * @param {object} competencyTree
 * @param {number} competencyItemId
 * @param {object} competencyDocument
 * @returns {object} - new competency document item.
 */
export const addExpandedParentItemNode = (competencyTree, competencyItemId, competencyDocument) => {
  const currentItemChildrens = competencyTree.getChildren(competencyItemId)
    .sort(compareDocumentItems);
  let newItemPrefixNumber = 1;
  if (currentItemChildrens.length > 0) {
    newItemPrefixNumber = calculatePrefixNumber( 
      competencyTree,
      currentItemChildrens[0]
    );
  }

  return createCompetencyItemObject(
    newItemPrefixNumber,
    false, //create child item
    competencyTree,
    competencyDocument,
    competencyItemId,
  );
};

/**
 * calculate new titem prefix for ordering position
 * 
 * @param {Array} competencyItems
 * @param {object} competencyDocumentItem
 * @param {object} competencyTree
 * @returns {number} - prefix position number
 */
export const calculateNewItemPrefix = (
  competencyItems, 
  competencyDocumentItem, 
  competencyTree,
) => {
  const itemIndex = competencyItems.findIndex((item) => item.id === competencyDocumentItem.id);
  //If item is last child/parent then we are increasing prefix by 1
  if (itemIndex === competencyItems.length-1) { 
    return competencyDocumentItem.prefixDecimal + 1;
  } 
  const nextCompetencyItem = competencyItems[itemIndex + 1];
  //If item is not last child/Parent then we are calculating prefix number by passing
  // next item to the calculatePrefixNumber function
  return calculatePrefixNumber( 
    competencyTree,            
    nextCompetencyItem,
  );
};

/**
 * create new competenct document item
 * 
 * @param {number} prefixDecimalNumber
 * @param {boolean} isParentItem
 * @param {number} currentParentId
 * @param {object} competencyTree
 * @param {object} competencyDocument
 * @returns {object} - Newly created competency item object
 */
export const createCompetencyItemObject = (
  prefixDecimalNumber, 
  isParentItem,
  competencyTree,
  competencyDocument,
  currentParentId, 
) => {
  const {  
    COMPETENCY_DOCUMENT_ITEM_PARENT_KEY,
    COMPETENCY_DOCUMENT_ITEM_CHILD_KEY,
  } = competencyDocumentItemPrefixes;
  const { 
    UNDER_SCORE,
  } = competencyDocumentItemSufixes;

  let abbreviatedStatement = '',
    titleKey = '';
  if (isParentItem) {
    //create parent prefix.
    titleKey = `${COMPETENCY_DOCUMENT_ITEM_PARENT_KEY}${prefixDecimalNumber}`;
    abbreviatedStatement = `${intl.formatMessage(messages.parentItemTitle)}`;
  } else {
    const parentItem = competencyTree.itemsById[currentParentId];
    const childKeyWithPrefixDecimal = `${COMPETENCY_DOCUMENT_ITEM_CHILD_KEY}${prefixDecimalNumber}`;
    //create child prefix by using parent prefix.
    titleKey = `${parentItem.titleKey}${UNDER_SCORE}${ childKeyWithPrefixDecimal }`;
    abbreviatedStatement = `${intl.formatMessage(messages.childItemTitle)}`;
  }
  
  return appendDuplicateNumber({ 
    abbreviatedStatement, 
    humanCodingScheme: '', 
    fullStatement: '', 
    titleKey,
    currentParentId,
  }, competencyDocument);
};

/**
 * appends duplicate number with object title
 * 
 * @param {object} newCompetencyDocumentItem
 * @param {object} competencyDocument
 * @return {object} - Object with duplicate number appended to title
 * 
 */
export const appendDuplicateNumber = (newCompetencyDocumentItem, competencyDocument) => {
  const {
    ABBREVIATEDSTATEMENT,
  } = knowledgeMapDetailInputStates;
  
  //append duplicate number, If title already exists.
  newCompetencyDocumentItem[ABBREVIATEDSTATEMENT] = buildCompetencyDocumentCopyText(
    newCompetencyDocumentItem[ABBREVIATEDSTATEMENT], 
    newCompetencyDocumentItem,
    competencyDocument,
    false,
    true,
  );

  return newCompetencyDocumentItem;
};